import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { FLAGSHIP_ORG } from '@/lib/moduleAccess';
import { hostRedirect, handoffUrl } from '@/lib/tenantHost';

// ── OrgContext ───────────────────────────────────────────────────────────────
// WHICH TENANT IS THIS? Every entitlement gate used to answer "parcelink",
// because that was the only possible answer. With paying tenants it has to be
// resolved for real, and this is the one place that does it.
//
// The org comes from the SESSION (public.my_org(), which reads
// current_org_id()), never from the hostname. A hostname is a label anyone can
// type; the JWT is the thing RLS already trusts. So a tenant reaching the app
// on any host still sees exactly their own org, and someone typing another
// tenant's subdomain gains nothing. Hostname is used only for pre-login
// branding, which is cosmetic by definition.
//
// Fail-safe: no session at all → the flagship org, which is exactly today's
// behaviour for public pages. A signed-in user whose lookup keeps failing
// falls back to the last known good org, and to flagship only if there never
// was one — logged loudly. That fallback is cosmetic: it can only affect which
// module tiles render, because the data behind them is fenced by RLS on the
// server, where current_org_id() has no such fallback.
const OrgContext = createContext(undefined);

const RETRIES = 3;

export function OrgProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [org, setOrg] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const lastGood = useRef(null);

  useEffect(() => {
    let cancelled = false;

    // No signed-in user: public/anonymous surface. Behave as before.
    if (authLoading) return undefined;
    if (!user) {
      setOrg(null);
      setLoaded(true);
      return undefined;
    }

    const load = async (attempt = 0) => {
      const { data, error } = await supabase.rpc('my_org');
      if (cancelled) return;

      const row = Array.isArray(data) ? data[0] : data;
      if (!error && row?.org_key) {
        lastGood.current = row;
        setOrg(row);
        setLoaded(true);
        return;
      }

      if (attempt < RETRIES) {
        setTimeout(() => { if (!cancelled) load(attempt + 1); }, 400 * (attempt + 1));
        return;
      }

      console.error(
        '[org] could not resolve the current org after retries',
        error?.message ?? 'no row returned',
      );
      setOrg(lastGood.current);
      setLoaded(true);
    };

    setLoaded(false);
    load();
    return () => { cancelled = true; };
  }, [user, authLoading]);

  // Whose host is this? Parcelink's people belong on parcelink.org and a
  // tenant's on its Runmark host or its own domain once that answers
  // (src/lib/tenantHost.js). The org still comes from the session; this only
  // moves a signed-in person to the address that is theirs, keeping the path
  // they were on and carrying the session with them (handoffUrl), because
  // storage is per origin and a bare redirect would land them signed out.
  useEffect(() => {
    if (!loaded || !org || typeof window === 'undefined') return undefined;
    const target = hostRedirect(window.location.hostname, org);
    if (!target) return undefined;
    let cancelled = false;
    supabase.auth.getSession()
      .then(({ data }) => data?.session ?? null)
      .catch(() => null)
      .then((session) => {
        if (cancelled) return;
        window.location.replace(handoffUrl(target, window.location, session));
      });
    return () => { cancelled = true; };
  }, [loaded, org]);

  const value = useMemo(() => ({
    org,
    // The key every gate asks for. Flagship while anonymous or unresolved so
    // nothing that works today starts failing.
    orgKey: org?.org_key ?? FLAGSHIP_ORG,
    orgId: org?.id ?? null,
    orgName: org?.name ?? null,
    branding: org?.branding ?? {},
    isFlagship: (org?.org_key ?? FLAGSHIP_ORG) === FLAGSHIP_ORG,
    loaded,
  }), [org, loaded]);

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

// Safe outside a provider (storybook, isolated tests, the public shells that
// mount before OrgProvider): returns the flagship default rather than throwing.
export default function useOrg() {
  const ctx = useContext(OrgContext);
  if (ctx === undefined) {
    return {
      org: null, orgKey: FLAGSHIP_ORG, orgId: null, orgName: null,
      branding: {}, isFlagship: true, loaded: true,
    };
  }
  return ctx;
}
